import { useMemo } from 'react';

import { useAppSelector } from '../../../../lib/store';
import {
  useGetAllocationSummaryQuery,
  useGetDataSplitSummaryQuery,
} from '../../api/createProjectApiSlice';
import { AddedConcept } from '../../state/createProjectSlice';
import {
  selectConditionArea,
  selectProjectName,
  selectHealthSystem,
  selectSelectedAnnotators,
  selectSelectedSupervisors,
  selectAddedConcepts,
  selectWorkflowId,
  selectDefaultAllocationMode,
} from '../../state/selector';
import { AllocationSummaryResponse } from '../../types/response';

interface UseReviewAndCreateResults {
  projectName: string;
  conditionAreaName: string;
  healthSystemName: string;
  annotatorNames: string[];
  supervisorNames: string[];
  addedConcepts: AddedConcept[];
  validationEncounterCount: number;
  testEncounterCount: number;
  trainingEncounterCount: number;
  allocationSummary: AllocationSummaryResponse[];
  isAllocationSummaryLoading: boolean;
  isDataSplitSummaryLoading: boolean;
}
export const useReviewAndCreate = (): UseReviewAndCreateResults => {
  const projectName = useAppSelector(selectProjectName);
  const conditionArea = useAppSelector(selectConditionArea);
  const healthSystem = useAppSelector(selectHealthSystem);
  const selectedAnnotators = useAppSelector(selectSelectedAnnotators);
  const selectedSupervisors = useAppSelector(selectSelectedSupervisors);
  const addedConcepts = useAppSelector(selectAddedConcepts);
  const workflowId = useAppSelector(selectWorkflowId);
  const defaultAllocationMode = useAppSelector(selectDefaultAllocationMode);
  const {
    data: dataSplitSummaryData,
    isLoading: isDataSplitSummaryLoading,
    isFetching: isDataSplitSummaryFetching,
  } = useGetDataSplitSummaryQuery({ workflowId: workflowId ?? '' });
  const dataSplitSummary = useMemo(
    () => dataSplitSummaryData?.data ?? { trainingCount: 0, testCount: 0, validationCount: 0 },
    [dataSplitSummaryData]
  );

  const validationEncounterCount = dataSplitSummary.validationCount;
  const testEncounterCount = dataSplitSummary.testCount;
  const trainingEncounterCount = dataSplitSummary.trainingCount;

  const {
    data: allocationSummaryData,
    isLoading: isAllocationSummaryLoading,
    isFetching: isAllocationSummaryFetching,
  } = useGetAllocationSummaryQuery({
    selectedAnnotators,
    defaultAllocationMode,
    workflowId: workflowId ?? '',
  });
  const allocationSummary = useMemo(
    () => allocationSummaryData?.data ?? [],
    [allocationSummaryData]
  );
  return {
    projectName,
    conditionAreaName: conditionArea?.label ?? '',
    healthSystemName: healthSystem?.label ?? '',
    annotatorNames: selectedAnnotators.map((annotator) => annotator.label),
    supervisorNames: selectedSupervisors.map((supervisor) => supervisor.label),
    addedConcepts,
    validationEncounterCount,
    testEncounterCount,
    trainingEncounterCount,
    allocationSummary,
    isDataSplitSummaryLoading: isDataSplitSummaryLoading || isDataSplitSummaryFetching,
    isAllocationSummaryLoading: isAllocationSummaryLoading || isAllocationSummaryFetching,
  };
};
